// eslint-disable-next-line no-restricted-imports -- This is the only place where we import from react-router-dom
import { generatePath, useParams } from "react-router-dom";
import {
  MemberProfileTab,
  CompanyProfileTab,
  MainRoutes,
  ProjectProfileTab,
  RouteParams,
} from "@router/route-params";
import {
  PreservedSearchProps,
  usePreservedSearch,
} from "@hooks/use-preserved-search";
import { EAppRoutes, GenerateRouteReturn } from "@router/router-helper-types";

/** Use throughout app instead of plain `useParams` to get type-safety */
export function useAppParams(): Readonly<Partial<RouteParams>> {
  return useParams<RouteParams>();
}

/** List of routes available in the app */
export const AppRoutes: Record<EAppRoutes, string> = {
  /** The route of home page */
  [EAppRoutes.HomePage]: MainRoutes.root,

  /** The route of company with dynamic segments */
  [EAppRoutes.CompanyRoute]: `/${MainRoutes.company}/:companyId/:companyProfileTab`,

  /** The route of adding plan to company with dynamic segments */
  [EAppRoutes.CompanyAddPlanRoute]: `/${MainRoutes.company}/:companyId/add-plan`,

  /** The route of adding a feature or bundle to company with dynamic segments */
  [EAppRoutes.CompanyAddFeatureRoute]: `/${MainRoutes.company}/:companyId/add-feature`,

  /** The route of adding device plan to company with dynamic segments */
  [EAppRoutes.CompanyAddDevicePlanRoute]: `/${MainRoutes.company}/:companyId/add-device-plan`,

  /** The route of copying a plan of company with dynamic segments */
  [EAppRoutes.CompanyCopyPlanRoute]: `/${MainRoutes.company}/:companyId/copy-plan/:planId`,

  /** The route of adding project to company with dynamic segments */
  [EAppRoutes.CompanyAddProjectRoute]: `/${MainRoutes.company}/:companyId/group/:groupId/add-project`,

  /** The route of project with dynamic segments */
  [EAppRoutes.ProjectRoute]: `/${MainRoutes.project}/:projectId/:projectProfileTab`,

  /** The route of adding plan to project with dynamic segments */
  [EAppRoutes.ProjectAddPlanRoute]: `/${MainRoutes.project}/:projectId/add-plan`,

  /** The route of adding a feature or bundle to project with dynamic segments */
  [EAppRoutes.ProjectAddFeatureRoute]: `/${MainRoutes.project}/:projectId/add-feature`,

  /** The route of copying a plan of project with dynamic segments */
  [EAppRoutes.ProjectCopyPlanRoute]: `/${MainRoutes.project}/:projectId/copy-plan/:planId`,

  /** The route of member with dynamic segments */
  [EAppRoutes.MemberRoute]: `/${MainRoutes.member}/:memberId/:memberProfileTab`,

  /** The route of about products */
  [EAppRoutes.AboutProductsRoute]: `/${MainRoutes.aboutProducts}`,

  /** The route of testing impersonation */
  [EAppRoutes.TestingImpersonationRoute]: `/${MainRoutes.testingImpersonation}`,
};

/** Returns functions that generate route objects ({ pathname, search }) - they build the URL but don't navigate anywhere */
export function useGenerateRoutes(): GenerateRouteReturn {
  const { preservedSearch } = usePreservedSearch();

  /** Generates the home route */
  function generateHomeRoute(): PreservedSearchProps {
    return preservedSearch(generatePath(AppRoutes.HomePage));
  }

  /** Generates the company route */
  function generateCompanyRoute({
    companyId,
    companyProfileTab = CompanyProfileTab.companyInformation,
  }: Parameters<
    GenerateRouteReturn["generateCompanyRoute"]
  >[0]): PreservedSearchProps {
    return preservedSearch(
      generatePath(AppRoutes.CompanyRoute, {
        companyId,
        companyProfileTab,
      })
    );
  }

  /** Generates the company add plan route */
  function generateCompanyAddPlanRoute({
    companyId,
  }: Parameters<
    GenerateRouteReturn["generateCompanyAddPlanRoute"]
  >[0]): PreservedSearchProps {
    return preservedSearch(
      generatePath(AppRoutes.CompanyAddPlanRoute, {
        companyId,
      })
    );
  }

  /** Generates the company add feature route */
  function generateCompanyAddFeatureRoute({
    companyId,
  }: Parameters<
    GenerateRouteReturn["generateCompanyAddFeatureRoute"]
  >[0]): PreservedSearchProps {
    return preservedSearch(
      generatePath(AppRoutes.CompanyAddFeatureRoute, {
        companyId,
      })
    );
  }

  /** Generates the company add device plan route */
  function generateCompanyAddDevicePlanRoute({
    companyId,
  }: Parameters<
    GenerateRouteReturn["generateCompanyAddDevicePlanRoute"]
  >[0]): PreservedSearchProps {
    return preservedSearch(
      generatePath(AppRoutes.CompanyAddDevicePlanRoute, {
        companyId,
      })
    );
  }

  /** Generates the company copy plan route */
  function generateCompanyCopyPlanRoute({
    companyId,
    planId,
  }: Parameters<
    GenerateRouteReturn["generateCompanyCopyPlanRoute"]
  >[0]): PreservedSearchProps {
    return preservedSearch(
      generatePath(AppRoutes.CompanyCopyPlanRoute, {
        companyId,
        planId,
      })
    );
  }

  /** Generates the company add project route */
  function generateCompanyAddProjectPlanRoute({
    companyId,
    groupId,
  }: Parameters<
    GenerateRouteReturn["generateCompanyAddProjectPlanRoute"]
  >[0]): PreservedSearchProps {
    return preservedSearch(
      generatePath(AppRoutes.CompanyAddProjectRoute, {
        companyId,
        groupId,
      })
    );
  }

  /** Generates the project route */
  function generateProjectRoute({
    projectId,
    projectProfileTab = ProjectProfileTab.projectInformation,
  }: Parameters<
    GenerateRouteReturn["generateProjectRoute"]
  >[0]): PreservedSearchProps {
    return preservedSearch(
      generatePath(AppRoutes.ProjectRoute, {
        projectId,
        projectProfileTab,
      })
    );
  }

  /** Generates the project add plan route */
  function generateProjectAddPlanRoute({
    projectId,
  }: Parameters<
    GenerateRouteReturn["generateProjectAddPlanRoute"]
  >[0]): PreservedSearchProps {
    return preservedSearch(
      generatePath(AppRoutes.ProjectAddPlanRoute, {
        projectId,
      })
    );
  }

  /** Generates the project add feature route */
  function generateProjectAddFeatureRoute({
    projectId,
  }: Parameters<
    GenerateRouteReturn["generateProjectAddFeatureRoute"]
  >[0]): PreservedSearchProps {
    return preservedSearch(
      generatePath(AppRoutes.ProjectAddFeatureRoute, {
        projectId,
      })
    );
  }

  /** Generates the project copy plan route */
  function generateProjectCopyPlanRoute({
    projectId,
    planId,
  }: Parameters<
    GenerateRouteReturn["generateProjectCopyPlanRoute"]
  >[0]): PreservedSearchProps {
    return preservedSearch(
      generatePath(AppRoutes.ProjectCopyPlanRoute, {
        projectId,
        planId,
      })
    );
  }

  /** Generates the member route */
  function generateMemberRoute({
    memberId,
    memberProfileTab = MemberProfileTab.memberInformation,
  }: Parameters<
    GenerateRouteReturn["generateMemberRoute"]
  >[0]): PreservedSearchProps {
    return preservedSearch(
      generatePath(AppRoutes.MemberRoute, {
        memberId,
        memberProfileTab,
      })
    );
  }

  /** Generates the about products route */
  function generateAboutProductsRoute(): PreservedSearchProps {
    return preservedSearch(generatePath(AppRoutes.AboutProductsRoute));
  }

  /** Generates the testing impersonation route */
  function generateTestingImpersonationRoute(): PreservedSearchProps {
    return preservedSearch(generatePath(AppRoutes.TestingImpersonationRoute));
  }

  return {
    generateHomeRoute,
    generateCompanyRoute,
    generateCompanyAddPlanRoute,
    generateCompanyAddFeatureRoute,
    generateCompanyAddDevicePlanRoute,
    generateCompanyCopyPlanRoute,
    generateCompanyAddProjectPlanRoute,
    generateProjectRoute,
    generateProjectAddPlanRoute,
    generateProjectAddFeatureRoute,
    generateProjectCopyPlanRoute,
    generateMemberRoute,
    generateAboutProductsRoute,
    generateTestingImpersonationRoute,
  };
}
